import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  Typography,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const SalePaymentAdd = ({ open, onClose, token, onSuccess }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd(
    "shop/customer-balance-payments",
    token
  );

  const { data: customersData, loading: customersLoading } = useFetchObjects(
    ["customers"],
    "shop/customers?page_size=all",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies",
    token
  );

  const customers = customersData?.results || customersData || [];
  const currencies = currenciesData?.results || currenciesData || [];

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 16)
  );
  const [notes, setNotes] = useState("");

  const handleSubmit = () => {
    if (!selectedCustomer?.id || !selectedCurrency?.id || !amount) return;
    const payload = {
      customer: selectedCustomer.id,
      currency: selectedCurrency.id,
      amount: Number(amount),
      payment_date: paymentDate,
      notes: notes.trim() || undefined,
    };
    handleAdd(payload);
  };

  useEffect(() => {
    if (resetForm) {
      setSelectedCustomer(null);
      setSelectedCurrency(null);
      setAmount("");
      setPaymentDate(new Date().toISOString().slice(0, 16));
      setNotes("");
      onSuccess?.();
      onClose();
    }
  }, [resetForm, onClose, onSuccess]);

  const currentBalance = selectedCustomer?.total_owed_by_currency;
  const balanceStr =
    currentBalance && typeof currentBalance === "object"
      ? Object.entries(currentBalance)
          .map(([cur, amt]) => `${Number(amt).toFixed(2)} ${cur}`)
          .join(", ") || "0"
      : "0";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("salePayment.recordPayment", "Record payment (reduces balance)")}
      </DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <Autocomplete
            value={selectedCustomer}
            onChange={(e, newValue) => setSelectedCustomer(newValue)}
            options={customers || []}
            getOptionLabel={(option) => option.name || "—"}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            loading={customersLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("salePayment.customer", "Customer")}
                margin="dense"
                fullWidth
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {customersLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {selectedCustomer && (
            <Box mt={1} p={2} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="body2" color="textSecondary">
                {t("salePayment.currentBalance", "Current balance")}:{" "}
                <strong>{balanceStr}</strong>
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block">
                {t("salePayment.balanceReducedByPayment", "Payment will reduce this balance.")}
              </Typography>
            </Box>
          )}

          <Autocomplete
            value={selectedCurrency}
            onChange={(e, newValue) => setSelectedCurrency(newValue)}
            options={currencies || []}
            getOptionLabel={(option) => option.title || ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={currencyLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("salePayment.selectCurrency", "Currency")}
                margin="dense"
                fullWidth
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {currencyLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            margin="dense"
            label={t("salePayment.amount", "Amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            margin="dense"
            label={t("salePayment.paymentDate", "Payment date")}
            type="datetime-local"
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("salePayment.notes", "Notes")}
            multiline
            rows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("salePayment.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading || !selectedCustomer || !selectedCurrency || !amount
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("salePayment.submit", "Record payment")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalePaymentAdd;
